import Vue from 'vue'
import { Fornecedor, KitOpme } from '@/types/ui/solicitacao-cirurgia'
import { IProtocolo, IProtocoloKitOpme, IProtocoloKitDeOpmeV2 } from '@/types/interfaces/protocolo/protocolo.interface'
import { State, OrigemKitMateriais, KitPersonalizado } from './types'

export const kitOpmeStore = (() => {
  const state = Vue.observable<State>({
    protocolo: undefined,
    versao: undefined,
    origem: undefined,
    kits: [],
  })

  function converterKitV1ParaViewModel(raw: IProtocoloKitOpme): KitOpme {
    const fornecedoresProtocolo = state.protocolo ? state.protocolo.fornecedores : []

    return {
      id: raw.id,
      nome: raw.nome,
      tecnicaDeCirurgia: raw.tecnicaDeCirurgia
        ? {
          id: raw.tecnicaDeCirurgia.id,
          nome: raw.tecnicaDeCirurgia.nome,
        }
        : undefined,
      materiais: raw.opmes.map((opmeItem) => {
        const fornecedoresOrigem = (opmeItem.fornecedores && opmeItem.fornecedores.length > 0)
          ? opmeItem.fornecedores
          : fornecedoresProtocolo

        const fornecedores: Fornecedor[] = fornecedoresOrigem.map(fornecedor => ({
          id: fornecedor.fornecedorId,
          nome: fornecedor.fornecedorNome,
        }))

        return {
          id: opmeItem.id,
          nome: opmeItem.descricao,
          quantidade: opmeItem.quantidade,
          fornecedores,
        }
      }),
    }
  }

  function converterKitV2ParaViewModel(raw: IProtocoloKitDeOpmeV2): KitOpme {
    return {
      id: raw.id,
      nome: raw.nome,
      tecnicaDeCirurgia: raw.tecnicaDeCirurgia
        ? {
          id: raw.tecnicaDeCirurgia.id,
          nome: raw.tecnicaDeCirurgia.nome,
        }
        : undefined,
      materiais: raw.materiais.map((material: any) => {
        let materialId = ''
        if (
          material.materiaisCodificados
          && material.materiaisCodificados.length > 0
          && material.materiaisCodificados[0].id
        ) {
          materialId = material.materiaisCodificados[0].id
        }

        return {
          id: materialId,
          nome: material.nomeTecnico,
          quantidade: material.quantidade,
          quantidadeMinima: material.quantidadeMinima,
          quantidadeMaxima: material.quantidadeMaxima,
          medicoPodeAlterarAQuantidade: material.medicoPodeAlterarAQuantidade,
          grupoId: material.grupoDeMaterial ? material.grupoDeMaterial : undefined,
          fornecedores: material.fornecedores.map(f => ({
            id: f.fornecedorId,
            nome: f.fornecedorFabricante || f.fornecedorNome,
          })),
          especificidades: material.materiaisCodificados.map(m => m.especificidade || m.descricao),
        }
      }),
      grupos: raw.materiaisAgrupados && raw.materiaisAgrupados.length > 0
        ? raw.materiaisAgrupados.map((g: any) => ({
          id: g.id,
          nome: g.nome,
        }))
        : undefined,
    }
  }

  function converterKitPersonalizadoParaKitV1(kit): IProtocoloKitOpme {
    return {
      id: kit.id,
      nome: kit.descricao,
      tecnicaDeCirurgia: kit.tecnicaDeCirurgia,
      opmes: kit.opmes.map(({ opme, ...rest }) => {
        const fornecedores = ['fornecedor1', 'fornecedor2', 'fornecedor3'].reduce((acc, key) => {
          if (rest[key]) {
            acc.push({
              fornecedorId: rest[key].id,
              fornecedorNome: rest[key].nome,
            })
          }
          return acc
        }, [] as { fornecedorId: string, fornecedorNome: string }[])

        return {
          opme,
          ...opme,
          ...rest,
          fornecedores,
        }
      }),
    }
  }

  function identificarVersao(protocolo: IProtocolo) {
    const possuiKitsV1 = Array.isArray(protocolo.kitsDeOpme) && protocolo.kitsDeOpme.length > 0
    const possuiKitsV2 = Array.isArray(protocolo.kitsDeOpmeV2) && protocolo.kitsDeOpmeV2.length > 0

    if (possuiKitsV1) return 'v1'
    if (possuiKitsV2) return 'v2'

    if ('kitsDeOpmeV2' in protocolo) return 'v2'
    if ('kitsDeOpme' in protocolo) return 'v1'

    return undefined
  }

  function extrairKitsPorVersao(protocolo: IProtocolo, versao?: 'v1' | 'v2') {
    if (versao === 'v1') {
      return protocolo.kitsDeOpme
    }

    if (versao === 'v2') {
      return protocolo.kitsDeOpmeV2
    }

    return []
  }

  function processarKitsProtocolo(protocolo: IProtocolo) {
    const versao = identificarVersao(protocolo)
    const kits = extrairKitsPorVersao(protocolo, versao)

    if (kits) {
      state.protocolo = protocolo
      state.kits = kits
      state.versao = versao
      state.origem = OrigemKitMateriais.Protocolo
    }
  }

  function processarKitsPersonalizados(kits: KitPersonalizado[]) {
    state.protocolo = undefined
    state.versao = 'v1'
    state.kits = kits.map(converterKitPersonalizadoParaKitV1)
    state.origem = OrigemKitMateriais.Medico
  }

  function obterKitPorId(id: string) {
    if (state.versao === 'v1') {
      return state.kits.find(k => k.id === id)
    }

    if (state.versao === 'v2') {
      return state.kits.find(k => k.id === id)
    }

    return undefined
  }

  return {
    get origem() {
      return state.origem
    },
    get versao() {
      return state.versao
    },
    get kits(): KitOpme[] {
      if (state.versao === 'v1') {
        return state.kits.map(converterKitV1ParaViewModel)
      }

      if (state.versao === 'v2') {
        return state.kits.map(converterKitV2ParaViewModel)
      }

      return []
    },
    get protocolo() {
      return state.protocolo
    },
    obterKitPorId,
    processarKitsProtocolo,
    processarKitsPersonalizados,
  }
})()

export default kitOpmeStore
